import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Container, Row, Col, Spinner } from "reactstrap";
import { Helmet } from "react-helmet-async";
import { BlogDetail as getBlogDetail } from "../../api/blogService";
import "../../assets/css/blogdetail.css";

const BlogDetail = () => {
    const { slug } = useParams();
    const [blogDetail, setBlogDetail] = useState(null);
    const [categories, setCategories] = useState([]);
    const [recentBlogs, setRecentBlogs] = useState([]);
    const [navigation, setNavigation] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBlogDetail = async () => {
            try {
                if (!slug) {
                    console.warn("Slug not found in URL");
                    return;
                }

                const response = await getBlogDetail(slug);
                console.log("Blog Detail Response:", response);

                if (response?.Detail?.length > 0) {
                    setBlogDetail(response.Detail[0]);
                } else {
                    setBlogDetail(null);
                }

                if (response?.BlogCategory?.length > 0) {
                    setCategories(response.BlogCategory);
                }

                if (response?.RecentBlog?.length > 0) {
                    setRecentBlogs(response.RecentBlog);
                }

                if (response?.Navigation?.length > 0) {
                    setNavigation(response.Navigation);
                }
            } catch (error) {
                console.error("Error fetching blog detail:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchBlogDetail();
        window.scrollTo({ top: 0, behavior: "smooth" }); // UX improvement
    }, [slug]);

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner color="primary" />
            </div>
        );
    }

    if (!blogDetail) {
        return (
            <div className="text-center py-5 text-muted">
                No blog details found.
            </div>
        );
    }

    const decodeHTML = (html) => {
        const txt = document.createElement("textarea");
        txt.innerHTML = html;
        return txt.value;
    };

    // Fallback image for SEO and sharing
    const fallbackImg = "/assets/images/noImage/no-image3.png";
    const imageUrl =
        blogDetail.IMAGEURL && blogDetail.IMAGEURL.trim() !== ""
            ? blogDetail.IMAGEURL
            : fallbackImg;

    return (
        <>
            {/* SEO META TAGS */}
            <Helmet>
                <title>{blogDetail.METATITLE || blogDetail.BLOGTITLE}</title>
                <meta
                    name="description"
                    content={
                        blogDetail.METADESCRIPTION ||
                        blogDetail.BLOGDESCRIPTION?.slice(0, 150)
                    }
                />
                <meta
                    name="keywords"
                    content={
                        blogDetail.METATAG ||
                        "blog, article, learning, jewelry, tips"
                    }
                />
                <link rel="canonical" href={window.location.href} />

                {/* Open Graph (Facebook / LinkedIn / etc.) */}
                <meta property="og:type" content="article" />
                <meta
                    property="og:title"
                    content={blogDetail.METATITLE || blogDetail.BLOGTITLE}
                />
                <meta
                    property="og:description"
                    content={
                        blogDetail.METADESCRIPTION || blogDetail.BLOGDESCRIPTION
                    }
                />
                <meta property="og:image" content={imageUrl} />
                <meta property="og:url" content={window.location.href} />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content={blogDetail.METATITLE || blogDetail.BLOGTITLE}
                />
                <meta
                    name="twitter:description"
                    content={
                        blogDetail.METADESCRIPTION || blogDetail.BLOGDESCRIPTION
                    }
                />
                <meta name="twitter:image" content={imageUrl} />
            </Helmet>

            {/* Actual Blog Content */}
            <section className="blog-detail-section">
                <Container>
                    <Row className="p-2">
                        <Col lg="9" md="8" sm="12" data-aos="fade-right">
                            <h2 className="blog-detail-title fw-bold mb-3">
                                {blogDetail.BLOGTITLE}
                            </h2>

                            <div className="d-flex align-items-center text-muted mb-3 blog-meta">
                                <span>By {blogDetail.POSTBY}</span>
                                <span className="mx-3">|</span>
                                <span>
                                    {new Date(blogDetail.POSTDATE).toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "long",
                                        day: "numeric",
                                    })}
                                </span>
                            </div>

                            <div className="blog-detail-image mb-4">
                                <img
                                    src={
                                        blogDetail.IMAGEURL && blogDetail.IMAGEURL !== "null"
                                            ? blogDetail.IMAGEURL
                                            : fallbackImg
                                    }
                                    alt={blogDetail.BLOGTITLE}
                                    className="img-fluid rounded"
                                    style={{
                                        width: "100%",
                                        height: "auto",
                                        objectFit: "contain",
                                    }}
                                    onError={(e) => {
                                        e.target.src = fallbackImg;
                                    }}
                                />
                            </div>

                            <p className="text-secondary mb-4">
                                {blogDetail.BLOGDESCRIPTION}
                            </p>

                            <div
                                className="blog-detail-content"
                                dangerouslySetInnerHTML={{
                                    __html: decodeHTML(blogDetail.TEXT),
                                }}
                            ></div>

                            {/* <div className="blog-meta-section mt-5 p-3 bg-light rounded">
                                <p className="mb-1">
                                    <strong>Meta Title:</strong> {blogDetail.METATITLE}
                                </p>
                                <p className="mb-1">
                                    <strong>Meta Description:</strong>{" "}
                                    {blogDetail.METADESCRIPTION}
                                </p>
                                <p>
                                    <strong>Meta Tags:</strong> {blogDetail.METATAG}
                                </p>
                            </div> */}

                            {navigation && navigation.length > 0 && (
                                <div className="blog-navigation d-flex justify-content-between mt-lg-5 border-top pt-4">
                                    {navigation.some((n) => n.POSTTYPE === "Previous") ? (
                                        navigation
                                            .filter((n) => n.POSTTYPE === "Previous")
                                            .map((prev, i) => (
                                                <Link
                                                    key={i}
                                                    to={`/learn/blog/${prev.SLUG}`}
                                                    className="text-decoration-none text-primary"
                                                >
                                                    <i className="bx bx-left-arrow-alt"></i>{" "}
                                                    {prev.BLOGTITLE}
                                                </Link>
                                            ))
                                    ) : (
                                        <span></span>
                                    )}

                                    {navigation.some((n) => n.POSTTYPE === "Next") ? (
                                        navigation
                                            .filter((n) => n.POSTTYPE === "Next")
                                            .map((next, i) => (
                                                <Link
                                                    key={i}
                                                    to={`/learn/blog/${next.SLUG}`}
                                                    className="text-decoration-none text-primary ms-auto text-end"
                                                >
                                                    {next.BLOGTITLE}{" "}
                                                    <i className="bx bx-right-arrow-alt"></i>
                                                </Link>
                                            ))
                                    ) : (
                                        <span></span>
                                    )}
                                </div>
                            )}
                        </Col>

                        {/* Sidebar */}
                        <Col lg="3" md="4" sm="12" data-aos="fade-left">
                            <div className="p-3 bg-white shadow-sm rounded category-sidebar mb-4">
                                <h5 className="fw-bold border-bottom pb-2 mb-3">
                                    Categories
                                </h5>
                                <ul className="list-unstyled mb-0 blog-list">
                                    {categories.map((cat, i) => (
                                        <li key={i} className="mb-2">
                                            <Link
                                                to={`/learn?category=${encodeURIComponent(
                                                    cat.VALUE.trim()
                                                )}`}
                                                className="text-decoration-none text-primary small collection-item" 
                                            >
                                                <i className="bx bx-wifi-0 fs-4"></i>{" "}
                                                {cat.VALUE.trim()}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>
        </>
    );
};

export default BlogDetail;
